import { motion } from "framer-motion";
import {
  Sparkles,
  Database,
  Play,
  Wand2,
  BarChart3,
  TableProperties,
  Bot,
} from "lucide-react";
import MockFrame from "./MockFrame";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

// SQL syntax tints — chosen to read well on a dark surface; keywords lean
// into the violet accent so the panel feels native to MagixDB.
const SQL = { kw: "#c084fc", id: "#f3f4f6", str: "#86efac", num: "#67e8f9", fn: "#fde68a", muted: "rgba(255,255,255,0.4)" };

function K({ children }) {
  return <span style={{ color: SQL.kw, fontWeight: 600 }}>{children}</span>;
}
function I({ children }) {
  return <span style={{ color: SQL.id }}>{children}</span>;
}
function S({ children }) {
  return <span style={{ color: SQL.str }}>{children}</span>;
}
function N({ children }) {
  return <span style={{ color: SQL.num }}>{children}</span>;
}
function Fn({ children }) {
  return <span style={{ color: SQL.fn }}>{children}</span>;
}

const results = [
  { name: "Acme Logistics", value: 92, revenue: "$1.84M" },
  { name: "Helix Biotech", value: 81, revenue: "$1.62M" },
  { name: "NorthStar Capital", value: 74, revenue: "$1.48M" },
  { name: "Vega Manufacturing", value: 68, revenue: "$1.36M" },
  { name: "Lumen Health", value: 58, revenue: "$1.16M" },
];

export default function MagixDBVisual({ accent }) {
  return (
    <MockFrame
      domain="magixdb.velozent.com / query"
      accent={accent}
      tabs={["Ask", "SQL", "Charts", "Connections"]}
      activeTab={0}
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="space-y-3"
      >
        <motion.div
          variants={fadeUp}
          custom={0}
          className="rounded-xl border border-white/5 bg-white/[0.025] p-3"
        >
          <div className="flex items-center gap-2">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1"
              style={{
                backgroundColor: accent.bg,
                color: accent.primary,
                borderColor: accent.border,
              }}
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.4} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/40">
                Ask in plain English
              </p>
              <p className="mt-0.5 truncate text-[12px] font-medium text-white/85">
                Show top 10 customers by revenue last quarter
                <motion.span
                  className="ml-0.5 inline-block h-3 w-[2px] align-middle"
                  style={{ backgroundColor: accent.primary }}
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  aria-hidden
                />
              </p>
            </div>
            <span
              className="hidden shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold sm:inline-flex"
              style={{
                backgroundColor: accent.bg,
                color: accent.light,
                borderColor: accent.border,
              }}
            >
              <Wand2 className="h-3 w-3" />
              Generate
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-5">
          <motion.div
            variants={fadeUp}
            custom={1}
            className="overflow-hidden rounded-xl border border-white/5 bg-[#0a0d14] lg:col-span-3"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-3 py-2">
              <div className="flex items-center gap-2">
                <Database className="h-3 w-3" style={{ color: accent.primary }} />
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">
                  Generated SQL
                </p>
              </div>
              <span
                className="inline-flex items-center gap-1 rounded-md border px-1.5 py-[2px] text-[9.5px] font-bold uppercase tracking-[0.1em]"
                style={{
                  backgroundColor: "rgba(52, 211, 153, 0.10)",
                  color: "#86efac",
                  borderColor: "rgba(52, 211, 153, 0.28)",
                }}
              >
                <Play className="h-2.5 w-2.5" />
                Run
              </span>
            </div>
            <pre className="overflow-x-auto px-3 py-2.5 text-[10.5px] leading-[1.55]" style={{ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace' }}>
              <code>
                <span style={{ color: SQL.muted }}>{`1 `}</span>
                <K>SELECT</K> <I>customer_name</I>,{"\n"}
                <span style={{ color: SQL.muted }}>{`2     `}</span>
                <Fn>SUM</Fn>(<I>amount</I>) <K>AS</K> <I>revenue</I>{"\n"}
                <span style={{ color: SQL.muted }}>{`3 `}</span>
                <K>FROM</K> <I>orders</I>{"\n"}
                <span style={{ color: SQL.muted }}>{`4 `}</span>
                <K>WHERE</K> <I>order_date</I> &gt;= <S>&apos;2025-07-01&apos;</S>{"\n"}
                <span style={{ color: SQL.muted }}>{`5 `}</span>
                <K>GROUP BY</K> <I>customer_name</I>{"\n"}
                <span style={{ color: SQL.muted }}>{`6 `}</span>
                <K>ORDER BY</K> <I>revenue</I> <K>DESC</K>{"\n"}
                <span style={{ color: SQL.muted }}>{`7 `}</span>
                <K>LIMIT</K> <N>10</N>;
              </code>
            </pre>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={2}
            className="rounded-xl border border-white/5 bg-white/[0.025] p-3 lg:col-span-2"
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50">
                Top customers
              </p>
              <span className="inline-flex items-center gap-1 text-[9.5px] font-bold text-white/50">
                <BarChart3 className="h-2.5 w-2.5" style={{ color: accent.primary }} />
                Q3 2025
              </span>
            </div>
            <ul className="mt-2.5 space-y-1.5">
              {results.map((r, i) => (
                <li key={r.name} className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="truncate text-white/75">{r.name}</span>
                    <span
                      className="shrink-0 font-semibold tabular-nums"
                      style={{ color: accent.light }}
                    >
                      {r.revenue}
                    </span>
                  </div>
                  <div className="h-1 overflow-hidden rounded-full bg-white/[0.04]">
                    <motion.div
                      className="h-full rounded-full"
                      style={{
                        background: `linear-gradient(90deg, ${accent.deep}, ${accent.primary})`,
                        boxShadow: `0 0 6px ${accent.glow}`,
                      }}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${r.value}%` }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{
                        duration: 0.9,
                        delay: 0.4 + i * 0.08,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={3}
          className="flex items-start gap-2.5 rounded-xl border border-white/5 bg-white/[0.025] p-3"
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1"
            style={{
              backgroundColor: accent.bg,
              color: accent.primary,
              borderColor: accent.border,
            }}
          >
            <Bot className="h-3.5 w-3.5" strokeWidth={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/40">
              MagixDB Assistant
            </p>
            <p className="mt-1 text-[10.5px] leading-[1.55] text-white/70">
              Joined <span className="text-white/90">orders</span> →{" "}
              <span className="text-white/90">customers</span> on{" "}
              <span style={{ color: accent.light }}>customer_id</span>, filtered to
              Q3 and ranked by total spend.{" "}
              <span className="text-white/45">142ms · cached</span>
            </p>
          </div>
          <span
            className="hidden shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[9.5px] font-bold sm:inline-flex"
            style={{
              backgroundColor: "rgba(255,255,255,0.03)",
              color: "rgba(255,255,255,0.7)",
              borderColor: "rgba(255,255,255,0.08)",
            }}
          >
            <TableProperties className="h-2.5 w-2.5" />
            Export
          </span>
        </motion.div>
      </motion.div>
    </MockFrame>
  );
}
